import { useCallback, useContext, useState } from 'react';

import type { ChatConversation } from '../../../../../../common';
import Authentication from './authentication';
import VerificationCode from './verificationCode';

import * as api from '../../../../../../api';
import { ConfigurationContext } from '../../../../../../context/configurationContext';

interface Props {
  message: ChatConversation;
  showTextAnimation: boolean;
  onReconciliationSuccess: (
    loginInfo: Pick<
      api.AuthPatient.AuthPatientBody,
      'firstName' | 'lastName' | 'dateOfBirth'
    >,
  ) => void;
  showMessageAnswer?: boolean;
}

const Reconciliation: React.FC<Props> = ({
  message,
  showTextAnimation,
  onReconciliationSuccess,
  showMessageAnswer,
}) => {
  const configurationContext = useContext(ConfigurationContext);
  const [provisionalPatientId, setProvisionalPatientId] = useState<
    string | null
  >(null);
  const [submittedValues, setSubmittingValues] = useState<
    api.AuthPatient.AuthPatientBody | undefined
  >();

  const handleReconciliationSuccess = useCallback(async () => {
    if (!configurationContext?.id) return;
    if (!configurationContext.setAuthenticated) return;
    try {
      const { firstName, lastName, dateOfBirth } =
        await api.client.Assistant.me();
      configurationContext.setAuthenticated(true);
      onReconciliationSuccess({ firstName, lastName, dateOfBirth });
    } catch (error) {
      if (!submittedValues) {
        setProvisionalPatientId(null);
      } else {
        onReconciliationSuccess(submittedValues);
      }
      console.error(error);
    }
  }, [onReconciliationSuccess, configurationContext, submittedValues]);

  const commonProps = { message, showTextAnimation, showMessageAnswer };

  if (provisionalPatientId) {
    return (
      <VerificationCode
        provisionalPatientId={provisionalPatientId}
        onReconciliationSuccess={handleReconciliationSuccess}
        {...commonProps}
      />
    );
  }

  return (
    <Authentication
      onReconciliationSuccess={handleReconciliationSuccess}
      onContactVerificationRequired={(
        provisionalPatientId: string,
        loginInfo: api.AuthPatient.AuthPatientBody,
      ) => {
        setProvisionalPatientId(provisionalPatientId);
        setSubmittingValues(loginInfo);
      }}
      {...commonProps}
    />
  );
};

export default Reconciliation;
